import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './DisciplineScore.css';
import CreateEvent from './components/CreateEvent';
import EditEvent from './components/EditEvent';
import EventCard from './components/EventCard';
import EditShooters from './components/EditShooters';
import ScoreSheetManagement from './components/ScoreSheetManagement';
import FunShootQuickLaunch from './components/FunShootQuickLaunch';
import { 
    CircleDot, Plus, Target, Award, TrendingUp
} from 'lucide-react';
import { analyticsService, eventsService } from '../../../../../services';

const resolveEventType = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'rifle') return 'Rifle';
    if (normalized === 'game') return 'Game';
    return 'Clay';
};

const ClayScore = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedDiscipline, setSelectedDiscipline] = useState('all');
    const [showFormats, setShowFormats] = useState(false);
    const [events, setEvents] = useState([]);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editEventModalOpen, setEditEventModalOpen] = useState(false);
    const [editShootersModalOpen, setEditShootersModalOpen] = useState(false);
    const [scoreSheetManagementOpen, setScoreSheetManagementOpen] = useState(false);
    const [funShootQuickLaunchOpen, setFunShootQuickLaunchOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [editEventInitialView, setEditEventInitialView] = useState('details');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [shootersRefreshKey, setShootersRefreshKey] = useState(0);
    const [initialLaunchStandId, setInitialLaunchStandId] = useState(null);
    const [clayStats, setClayStats] = useState({
        totalTargets: 0,
        breakRate: 0,
        bestScore: 0,
        competition: {
            totalTargets: 0,
            eventsCount: 0
        },
        funShoot: {
            totalTargets: 0,
            eventsCount: 0
        }
    });

    const clayFormatGroups = {
        skeet: ['English Skeet', 'American Skeet', 'Olympic Skeet', 'Olympic Skeet (Pre-2012)', 'Olympic Skeet Final', 'Skeet Doubles', 'Skeet Shoot Off', 'Wobble Skeet'],
        sporting: ['Compak Sporting', 'English Sporting', 'FITASC Sporting', 'Five Stand', 'Sportrap', 'Super Sporting'],
        trap: ['American Trap', 'American Trap Doubles', 'Double Rise', 'Down The Line', 'Olympic Double Trap', 'Olympic Trap', 'Single Barrel', 'Wobble Trap (ABT)']
    };

    const clayDisciplines = [
        {
            id: 'all',
            name: 'All Formats',
            count: Object.values(clayFormatGroups).reduce((sum, formats) => sum + formats.length, 0)
        },
        { id: 'skeet', name: 'Skeet', count: clayFormatGroups.skeet.length },
        { id: 'sporting', name: 'Sporting', count: clayFormatGroups.sporting.length },
        { id: 'trap', name: 'Trap', count: clayFormatGroups.trap.length }
    ];

    const visibleFormats = selectedDiscipline === 'all'
        ? [...clayFormatGroups.skeet, ...clayFormatGroups.sporting, ...clayFormatGroups.trap]
        : clayFormatGroups[selectedDiscipline] || [];

    const fetchEvents = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await eventsService.getEvents('Clay');
            setEvents(data);
        } catch (error) {
            console.error('Error fetching events:', error);
            setError('Failed to load events. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    useEffect(() => {
        let isActive = true;

        const fetchClayStats = async () => {
            try {
                const analytics = await analyticsService.getAnalytics('month');
                if (!isActive) {
                    return;
                }

                setClayStats({
                    totalTargets: analytics?.claySummary?.totalTargets || 0,
                    breakRate: analytics?.claySummary?.breakRate || 0,
                    bestScore: analytics?.claySummary?.bestScore || 0,
                    competition: {
                        totalTargets: analytics?.claySummary?.competition?.totalTargets || 0,
                        eventsCount: analytics?.claySummary?.competition?.eventsCount || 0
                    },
                    funShoot: {
                        totalTargets: analytics?.claySummary?.funShoot?.totalTargets || 0,
                        eventsCount: analytics?.claySummary?.funShoot?.eventsCount || 0
                    }
                });
            } catch (statsError) {
                console.error('Error fetching clay stats:', statsError);
            }
        };

        fetchClayStats();

        return () => {
            isActive = false;
        };
    }, []);

    useEffect(() => {
        if (!events.length) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const eventId = params.get('eventId');
        const standId = params.get('standId');
        const launch = params.get('launch');

        if (!eventId || !standId || !launch) {
            return;
        }

        const matchingEvent = events.find((event) => String(event.eventId) === String(eventId));
        if (!matchingEvent) {
            return;
        }

        if (launch === 'fun') {
            openFunShootQuickLaunch(matchingEvent, standId);
        } else {
            openScoreSheetManagement(matchingEvent, standId);
        }

        navigate('/dashboard/score/clay', { replace: true });
    }, [events, location.search, navigate]);

    const closeCreateModal = () => setCreateModalOpen(false);
    const closeEditEventModal = () => setEditEventModalOpen(false);
    const openEditEventModal = (event, initialView = 'details') => {
        setSelectedEvent(event);
        setEditEventInitialView(initialView);
        setEditEventModalOpen(true);
    };
    const closeEditShootersModal = () => {
        setEditShootersModalOpen(false);
        setShootersRefreshKey(prevKey => prevKey + 1);
    };
    const openEditShootersModal = (event) => {
        setSelectedEvent(event);
        setEditShootersModalOpen(true);
    };
    const openScoreSheetManagement = (event, standId = null) => {
        setSelectedEvent(event);
        setInitialLaunchStandId(standId);
        setScoreSheetManagementOpen(true);
    };
    const closeScoreSheetManagement = () => setScoreSheetManagementOpen(false);
    const openFunShootQuickLaunch = (event, standId = null) => {
        setSelectedEvent(event);
        setInitialLaunchStandId(standId);
        setFunShootQuickLaunchOpen(true);
    };
    const closeFunShootQuickLaunch = () => setFunShootQuickLaunchOpen(false);

    const handleCreateEvent = (newEvent) => {
        setEvents(prevEvents => [newEvent, ...prevEvents]);
        closeCreateModal();
    };

    const handleUpdateEvent = async (updatedEvent) => {
        try {
            const updatedEventData = await eventsService.updateEvent(updatedEvent.eventId, updatedEvent);
            setEvents(prevEvents =>
                prevEvents.map(event =>
                    event.eventId === updatedEventData.eventId ? updatedEventData : event
                )
            );
            closeEditEventModal();
        } catch (error) {
            console.error('Error updating event:', error);
            setError('Failed to update event. Please try again.');
        }
    };

    const handleDeleteEvent = async (eventId) => {
        const event = events.find(e => e.eventId === eventId);
        if (window.confirm(`Are you sure you want to delete the event "${event?.name}"?`)) {
            try {
                await eventsService.deleteEvent(eventId);
                await fetchEvents();
            } catch (error) {
                console.error('Error deleting event:', error);
                setError('Failed to delete event. Please try again.');
            }
        }
    };

    const handleShootersUpdated = useCallback(() => {
        setShootersRefreshKey(prevKey => prevKey + 1);
    }, []);

    return (
        <div className="discipline-score-page">
            {/* Header */}
            <div className="discipline-header clay">
                <div className="discipline-header-content">
                    <div className="discipline-header-icon">
                        <CircleDot size={32} />
                    </div>
                    <div>
                        <h1>Clay Shooting</h1>
                        <p>Score your clay shoots - competition, practice, or fun days</p>
                    </div>
                </div>
                <button className="discipline-create-btn" onClick={() => setCreateModalOpen(true)}>
                    <Plus size={20} />
                    Create Event
                </button>
            </div>

            {/* Events Grid */}
            <div className="discipline-events-section">
                <div className="discipline-section-header">
                    <div>
                        <h3>Your Events</h3>
                        <p className="discipline-section-copy">Clay events stay front and centre, with scoring formats available when you need them.</p>
                    </div>
                </div>
                {isLoading ? (
                    <div className="discipline-loading">
                        <div className="loading-spinner"></div>
                        <p>Loading events...</p>
                    </div>
                ) : events.length === 0 ? (
                    <div className="discipline-empty">
                        <div className="empty-icon">
                            <CircleDot size={48} />
                        </div>
                        <h4>No events yet</h4>
                        <p>Create your first clay shooting event to start tracking your scores</p>
                        <button className="empty-create-btn" onClick={() => setCreateModalOpen(true)}>
                            <Plus size={20} />
                            Create Event
                        </button>
                    </div>
                ) : (
                    <div className="discipline-events-grid">
                        {events.map(event => (
                            <EventCard
                                key={event.eventId}
                                event={{
                                    id: event.eventId,
                                    name: event.name,
                                    eventTime: event.eventTime,
                                    eventDate: event.eventDate,
                                    clubName: event.clubName,
                                    clubId: event.clubId,
                                    eventType: resolveEventType(event.eventType ?? event.EventType),
                                    ownerName: event.ownerName,
                                    isOwner: event.isOwner,
                                    isShooter: event.isShooter,
                                    imageCount: event.imageCount || 0,
                                    coverImageUrl: event.coverImageUrl || null
                                }}
                                onEdit={() => openEditEventModal(event, 'details')}
                                onEditShooters={() => openEditShootersModal(event)}
                                onDelete={() => handleDeleteEvent(event.eventId)}
                                onManageScoreSheets={() => openScoreSheetManagement(event)}
                                onQuickFunShoot={() => openFunShootQuickLaunch(event)}
                                onManageMedia={() => openEditEventModal(event, 'media')}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Discipline Tabs */}
            <div className="discipline-tabs-section">
                <div className="discipline-tabs-header">
                    <div>
                        <h3>Clay Formats</h3>
                        <p className="discipline-section-copy">Keep this tucked away until you need a specific score format.</p>
                    </div>
                    <button className="discipline-collapse-btn" onClick={() => setShowFormats((current) => !current)}>
                        {showFormats ? 'Hide formats' : 'Show formats'}
                    </button>
                </div>
                {showFormats && (
                    <>
                        <div className="discipline-tabs clay">
                            {clayDisciplines.map(disc => (
                                <button
                                    key={disc.id}
                                    className={`discipline-tab ${selectedDiscipline === disc.id ? 'active' : ''}`}
                                    onClick={() => setSelectedDiscipline(disc.id)}
                                >
                                    {disc.name}
                                    <span className="tab-count">{disc.count}</span>
                                </button>
                            ))}
                        </div>
                        <div className="discipline-format-library">
                            <p>Structured clay scoring maps to the same core printable formats shown in Downloads.</p>
                            <div className="discipline-format-chips">
                                {visibleFormats.map((formatName) => (
                                    <span key={formatName} className="discipline-format-chip">{formatName}</span>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Error Message */}
            {error && (
                <div className="discipline-error">
                    {error}
                </div>
            )}

            {/* Stats Preview */}
            <div className="discipline-stats-preview">
                <h3>Your Clay Stats</h3>
                <div className="stats-preview-grid">
                    <div className="stat-preview-card">
                        <div className="stat-preview-icon orange">
                            <Target size={24} />
                        </div>
                        <div className="stat-preview-info">
                            <span className="stat-preview-value">{events.length}</span>
                            <span className="stat-preview-label">Events</span>
                        </div>
                    </div>
                    <div className="stat-preview-card">
                        <div className="stat-preview-icon orange">
                            <CircleDot size={24} />
                        </div>
                        <div className="stat-preview-info">
                            <span className="stat-preview-value">{clayStats.totalTargets}</span>
                            <span className="stat-preview-label">Targets Shot</span>
                        </div>
                    </div>
                    <div className="stat-preview-card">
                        <div className="stat-preview-icon green">
                            <TrendingUp size={24} />
                        </div>
                        <div className="stat-preview-info">
                            <span className="stat-preview-value">{clayStats.breakRate.toFixed(1)}%</span>
                            <span className="stat-preview-label">Avg. Hit Rate</span>
                        </div>
                    </div>
                    <div className="stat-preview-card">
                        <div className="stat-preview-icon blue">
                            <Award size={24} />
                        </div>
                        <div className="stat-preview-info">
                            <span className="stat-preview-value">{clayStats.bestScore}</span>
                            <span className="stat-preview-label">Best Score</span>
                        </div>
                    </div>
                </div>
                <div className="stats-preview-breakdown">
                    <div className="stats-preview-breakdown-card">
                        <span className="stats-preview-breakdown-label">Competition</span>
                        <strong>{clayStats.competition.totalTargets}</strong>
                        <small>{clayStats.competition.eventsCount} sheet(s) tracked</small>
                    </div>
                    <div className="stats-preview-breakdown-card">
                        <span className="stats-preview-breakdown-label">Fun Shoot</span>
                        <strong>{clayStats.funShoot.totalTargets}</strong>
                        <small>{clayStats.funShoot.eventsCount} sheet(s) tracked</small>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <CreateEvent
                isOpen={createModalOpen}
                closeModal={closeCreateModal}
                onEventCreated={handleCreateEvent}
            />
            <EditEvent
                isOpen={editEventModalOpen}
                closeModal={closeEditEventModal}
                event={selectedEvent}
                onEventUpdated={handleUpdateEvent}
                initialView={editEventInitialView}
                canEditDetails={selectedEvent?.isOwner !== false}
                canManageMedia={selectedEvent?.isOwner !== false}
            />
            <EditShooters
                isOpen={editShootersModalOpen}
                closeModal={closeEditShootersModal}
                event={selectedEvent}
                onShootersUpdated={handleShootersUpdated}
            />
            <ScoreSheetManagement
                eventId={selectedEvent?.eventId}
                isOpen={scoreSheetManagementOpen}
                onClose={closeScoreSheetManagement}
                refreshKey={shootersRefreshKey}
                canManage={selectedEvent?.isOwner !== false}
                initialStandId={initialLaunchStandId}
            />
            <FunShootQuickLaunch
                event={selectedEvent}
                isOpen={funShootQuickLaunchOpen}
                onClose={closeFunShootQuickLaunch}
                initialStandId={initialLaunchStandId}
            />
        </div>
    );
};

export default ClayScore;
